import React, { useCallback, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import OutlinedInput from '@mui/material/OutlinedInput';
import FaIconUvilo from './FaIconUvilo';
import InputAdornment from '@mui/material/InputAdornment';
import { type WithFilterProps } from '../types/WithFilterProps';
import useKeyboardEvents from '@acusti/use-keyboard-events';
import PowerIconButton from './PowerIconButton';
import debounce from 'lodash/debounce';

const StyledInput = styled(OutlinedInput, {
  shouldForwardProp: (prop) => prop !== 'fullWidthMode',
})<{ fullWidthMode?: boolean }>(({ theme, fullWidthMode }) => ({
  borderRadius: '25px',
  transition: theme.transitions.create('width', { duration: theme.transitions.duration.standard }),
  width: fullWidthMode ? '100%' : '10ch',
  ...(fullWidthMode
    ? {}
    : {
        '&.Mui-focused, &.has-value': {
          width: '100%',
        },
      }),
  '& .MuiOutlinedInput-root': {
    paddingRight: 10,
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 0px',
  },
}));

const ClearButton = styled(PowerIconButton)(({ theme }) => ({
  '&.MuiButtonBase-root': {
    marginRight: '-8px',
    width: '1.5rem',
    height: '1.5rem',
  },
  '&.Mui-focused, &:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

/**
 * A search box for filtering a list of items.
 *
 * Renders a rounded input that expands on focus or when it has a value,
 * with a clear button, Escape-to-clear handling, and an optional keyboard
 * shortcut to focus the input. Pass `fullWidth` to keep the input at 100%
 * of its container instead of expanding from a compact width.
 * @param props Component props.
 * @param props.defaultFilter Initial filter text shown in the input.
 * @param props.setFilter Called with the current filter text whenever it changes.
 * @param props.focusKey Key that focuses the input when pressed without modifiers.
 * @param props.debounceMs Delay in milliseconds before setFilter is called. Defaults to 500; pass 0 to disable debouncing.
 * @param props.fullWidth When true, the input width is always 100% of its container.
 * @returns The rendered search box input.
 */
export default function SearchBox({
  defaultFilter,
  setFilter,
  focusKey,
  debounceMs = 500,
  fullWidth = false,
}: WithFilterProps) {
  'use memo';

  const [value, setValue] = useState<string>(defaultFilter || '');
  const inputRef = useRef<HTMLInputElement>(null);
  if (debounceMs > 0) {
    setFilter = debounce(setFilter, debounceMs);
  }

  const clearSearch = (skipFocus?: boolean) => {
    setValue('');
    setFilter('');

    if (!skipFocus) {
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        if (value) {
          clearSearch(true);
        } else {
          inputRef.current?.blur();
        }
        event.stopPropagation();
        break;
    }
  };

  // keep: useKeyboardEvents requires stable onKeyDown reference
  const handleShortcutKeys = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key;

      switch (key) {
        case focusKey:
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          inputRef.current?.focus();
          event.preventDefault();
          break;
        case 'esc':
          clearSearch(true);
          event.preventDefault();
          break;
      }
    },
    [focusKey, clearSearch, inputRef],
  );

  useKeyboardEvents({
    ignoreUsedKeyboardEvents: true,
    onKeyDown: handleShortcutKeys,
  });

  return (
    <>
      <StyledInput
        className={value.length ? 'search-box has-value' : 'search-box'}
        fullWidthMode={fullWidth}
        margin="dense"
        value={value}
        onChange={async (event) => {
          const value = event.currentTarget.value;
          setValue(value);
          setFilter(value);
        }}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        startAdornment={
          <InputAdornment position="start" sx={{ color: 'text.secondary', pointerEvents: 'none' }}>
            <FaIconUvilo icon="magnifying-glass" />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment
            position="end"
            sx={{ color: 'text.secondary', ml: '2px', opacity: value.length ? 1 : 0 }}
          >
            <ClearButton
              title="Clear"
              size="small"
              edge="end"
              onClick={() => {
                clearSearch();
              }}
              icon="circle-xmark"
            />
          </InputAdornment>
        }
      />
    </>
  );
}
